import {environment} from 'environments/environment';
import {Component, HostListener, Injector, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ReCaptchaV3Service} from 'ng-recaptcha-2';
import {TranslateService} from '@ngx-translate/core';
import {firstValueFrom, Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, defaultIfEmpty, takeUntil} from 'rxjs/operators';
import {StateService} from '@uirouter/angular';
import {ButtonStyle} from 'app/components/ui/text-button/text-button.component';
import {STATE_NAMES} from '../../common/state-names';
import {CompanyGroupService} from '../../original-services/company-group.service';
import {FirstStepService} from '../../original-services/first-step.service';
import {RoutingService} from '../../original-services/routing.service';
import {HttpCancelService} from '../../original-services/httpCancel.service';
import {GAService} from '../../original-services/ga.service';
import {LoggerService} from '../../original-services/logger.service';
import {EMAIL_PATTERN} from '../../common/patterns';
import {SettingsService} from '../../original-services/settings.service';
import {StartingPage} from '../../common/constants';
import {PermissionsService} from '../../original-services/permissions.service';
import {AuthenticationService} from '../../original-services/authentication.service';
import {StorageService} from '../../original-services/storage.service';
import {SignIn} from '../../original-models/sign-in.model';
import {ColorThemeFromServerEnum} from '../../models/profile/color-theme.model';
import {ColorThemeService} from '../../services/profile/color-theme.service';
import {progressRoles} from 'app/original-models/access.model';
import {DefaultRouteService} from 'app/services/default-route.service';
import { IntercomService } from 'app/services/intercom/intercom.service';
import { AppShellPrefetchService } from 'app/services/app-shell/app-shell-prefetch.service';

@Component({
    selector: 'page-sign-in',
    templateUrl: './sign-in.component.html',
    standalone: false
})
export class SignInPageComponent implements OnInit, OnDestroy {
    transPrefix = 'SignIn.';
    formPrefix = 'Form.';
    buttonStyle = ButtonStyle;
    signUpState = STATE_NAMES.signUp;
    restoreEmail = STATE_NAMES.restoreEmail;
    errors: Observable<string>[] = [];
    submitPressed = false;
    submitPressesCount = 0;
    validationFailed = false;
    googleCaptchaKey: string;
    userActivate = <any>{};

    emailCtrl: UntypedFormControl;
    passwordCtrl: UntypedFormControl;
    rememberMeCtrl: UntypedFormControl;
    form: UntypedFormGroup;
    companiesLoaded: number;
    timer: any;
    isLoaded: boolean;
    // Kept as fields only because their handlers unsubscribe mid-flow, to stop after the first
    // usable emission. Everything else in this component tears down through destroy$.
    treeSubscription: Subscription;
    currentSubscription: Subscription;
    tokenCtrl: UntypedFormControl;
    tokenForm: UntypedFormGroup;
    validationTokenFailed = false;
    // Separate from `submitPressed`, which is already true by the time MFA kicks in — otherwise
    // the token field would show as invalid before the user has had a chance to type anything.
    tokenSubmitPressed = false;
    waitOtpToken = false;
    redirectUrl: string | null;

    private timeoutId: NodeJS.Timeout;

    private readonly destroy$ = new Subject<void>();

    // Raised before reCAPTCHA returns. isSubmitting disables the form, so a second Enter
    // would treat that disabled form as invalid and wipe the password mid-request.
    private submitInFlight = false;

    // The landing route is decided once. loadPermissions() hands back a BehaviorSubject that any
    // other caller can push to again, and failLogin is reachable from more than one stream, so
    // without this one sign-in could arm two navigations.
    private _routed = false;

    ACCESS: any;

    constructor(
        private authService: AuthenticationService,
        private companyGroupService: CompanyGroupService,
        private routingService: RoutingService,
        private settingsService: SettingsService,
        private logger: LoggerService,
        public translate: TranslateService,
        private storage: StorageService,
        private gaService: GAService,
        private firstStepService: FirstStepService,
        private httpCancelService: HttpCancelService,
        private stateService: StateService,
        private fb: UntypedFormBuilder,
        private injector: Injector,
        private storageService: StorageService,
        public intercom: IntercomService,
        private colorThemeService: ColorThemeService,
        private defaultRouteService: DefaultRouteService,
        private recaptchaV3Service: ReCaptchaV3Service,
        private renderer: Renderer2,
        private appShellPrefetch: AppShellPrefetchService
    ) {
        this.userActivate = {
            uid: this.routingService.params?.param1,
            token: this.routingService.params?.param2,
            activated: false
        };
        this.redirectUrl = this.routingService.queryString('redirectUrl') || null;
        this.emailCtrl = fb.control('', [Validators.required, Validators.pattern(EMAIL_PATTERN)]);
        this.passwordCtrl = fb.control('', [Validators.required]);
        //set to true, this property control later which storage to use local or session
        this.rememberMeCtrl = fb.control(false, []);
        this.form = fb.group({
            email: this.emailCtrl,
            password: this.passwordCtrl,
            rememberMe: this.rememberMeCtrl
        });

        this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.validationFailed = false;
        });

        // A rejected sign-in marks the password field and leaves the server's message under it.
        // Typing a new password clears that message, so a stale error never sits next to fresh
        // input. Every error is pushed after the last passwordCtrl mutation, so the resets in
        // evaluateErrors()/submitToken() can't wipe the message they are about to show.
        this.passwordCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.clearErrors();
        });
        this.httpCancelService.onCancelPendingRequests().pipe(takeUntil(this.destroy$)).subscribe(() => {
            clearTimeout(this.timer);
        });

        this.tokenCtrl = fb.control('', [Validators.required]);
        this.tokenForm = fb.group({
            otpToken: this.tokenCtrl
        });
        this.tokenForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            //We can clear errors when starting form edit
            this.validationTokenFailed = false;
        });
    }

    get isSubmitting(): boolean {
        return this.form.disabled;
    }

    set isSubmitting(value: boolean) {
        value ? this.form.disable() : this.form.enable();
    }

    get isTokenSubmitting(): boolean {
        return this.tokenForm.disabled;
    }

    set isTokenSubmitting(value: boolean) {
        value ? this.tokenForm.disable() : this.tokenForm.enable();
    }

    ngOnInit() {
        if (!this.settingsService.hideRecaptcha) {
            this.renderer.addClass(document.body, 'show-recaptcha');
        }
        this.activateUser();
        this.intercom.shutdown();
        this.gaService.trackPage(`/${this.stateService.current.name}${this.stateService.current.url}.html`);
        this.isLoaded = false;
    }

    /**
     * First typing into or press on a sign-in field (credentials or 2FA) is the cue to warm the
     * authenticated app chunk — see AppShellPrefetchService for why it is intent-triggered, limited to
     * this page, and bound to input/pointerdown rather than focus (the 2FA field has appAutofocus).
     */
    onFieldInteraction(event: Event): void {
        const target = event.target as HTMLElement | null;
        if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) {
            this.appShellPrefetch.warm();
        }
    }

    activateUser() {
        let uid = this.userActivate.uid;
        let token = this.userActivate.token;
        if (uid && token) {
            this.authService.activateTenant(uid, token).pipe(takeUntil(this.destroy$)).subscribe(data => {
                this.userActivate.activated = true;
            }, error => {
                this.logger.error(error);
            })
        }
    }

    resolved(token?: string) {
        let value = this.form.value;
        let model = new SignIn({
            email: value.email.toLowerCase(),
            password: value.password,
            recaptcha_response: token,
            rememberMe: value.rememberMe
        });
        this.isSubmitting = true;
        this.storage.menuClicked = null;
        this.authService.login(model).pipe(takeUntil(this.destroy$)).subscribe(data => {
            // An accepted password that still needs an OTP is not a completed sign-in.
            if (data && data.mfa_required) {
                return;
            }
            this.afterLogin();
        }, error => {
            this.isSubmitting = false;
            this.submitInFlight = false;
            if (error.error && error.error.mfa_required) {
                this.errors.length = 0;
                this.authService.processAccessToken(model.rememberMe, error.error.mfa_token);
                // The OTP field is focused by appAutofocus when its card appears.
                this.waitOtpToken = true;
                if (this.timeoutId) {
                    clearTimeout(this.timeoutId);
                }
                let time = 120;
                this.timeoutId = setTimeout(() => {
                    this.cancel();
                }, time * 1000);
            } else {
                this.evaluateErrors(error);
                this.logger.error(error);
            }
        });
    }

    /**
     * Safe to call straight from the /auth/token/ subscriber: AuthenticationService.processLoginData
     * persists the access token inside login()'s and auth2faToken()'s map(), so it has already run
     * by the time the value reaches us.
     *
     * This used to wait on StorageService.localItemChanged() instead, to see the token land. That
     * subject is a root singleton that never completes and fires on every storage write, and
     * processLoginData rewrites the token on each two-minute refresh, so a listener outliving its
     * submit replayed the whole account load (/subscription/, /corporate-groups/, /profile/) for as
     * long as the page stayed open.
     */
    private afterLogin() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
        this.isLoaded = false;
        this.storage.mfaToken = null;
        if (this.storage.model === 'tenant') {
            this.getSubscription();
        }
        if (this.storage.companyId) {
            this._getCompanies();
        } else {
            setTimeout(() => {
                this._getCompanies();
            }, 1000);
        }
    }

    private _getCompanies() {
        // No refresh: CompanyGroupService already reloads the tree off afterLogin$, which fires
        // from processLoginData before we get here. Forcing a second load ran the whole tree build
        // twice, and with it a second GET /corporate-groups/ plus a second setCurrentCompany, whose
        // updateCurrentCompany fetches the company and its members again.
        this.treeSubscription = this.companyGroupService.getTree(false)
            .pipe(takeUntil(this.destroy$))
            .subscribe((data) => {
            if (data) {
                if (this.treeSubscription) {
                    this.treeSubscription.unsubscribe();
                }

                this.currentSubscription = this.companyGroupService.getCurrent()
                    .pipe(takeUntil(this.destroy$))
                    .subscribe((company) => {
                    if (!this.storage.selectedLevel) {
                        return;
                    }
                    if (!this.isLoaded && ((data?.length && company) || !data.length)) {
                        this.isLoaded = true;
                        if (this.currentSubscription) {
                            this.currentSubscription.unsubscribe();
                        }

                        this.gaService.trackG4Event('login_GA4');

                        this.companiesLoaded = company ? company.id : null;
                        this.authService.getTenantProfile().pipe(
                            // Only the colour theme and the starting-page preference ride on this,
                            // and permissions have not even been requested yet, so a failure must
                            // not stop the chain. defaultIfEmpty covers getTenantProfile returning
                            // of() when userId is unset, which emits nothing at all.
                            catchError((error) => {
                                this.logger.error(error);
                                return of(null);
                            }),
                            defaultIfEmpty(null),
                            takeUntil(this.destroy$)
                        ).subscribe((profile) => {
                            if (profile) {
                                if (
                                    profile['ui_theme_color'] !== undefined &&
                                    profile['ui_theme_color'] !== null
                                ) {
                                    this.storageService.colorTheme = ColorThemeFromServerEnum[profile['ui_theme_color']];
                                    this.colorThemeService.setupColorTheme(ColorThemeFromServerEnum[profile['ui_theme_color']]);
                                }
                                // Inside the guard so that without a profile a preference stored in
                                // an earlier session is not overwritten with the default.
                                this.storage.startingPage = profile.home_page || StartingPage.Insights;
                            }
                            const permissionsService = this.injector.get(PermissionsService);
                            // No error callback here, nor on getTree above: both hand back a
                            // BehaviorSubject that never errors. CompanyGroupService turns every
                            // internal tree failure into another tree emission, and
                            // loadPermissions() resolves through _permissionsLoaded.
                            permissionsService.loadPermissions().pipe(takeUntil(this.destroy$)).subscribe(() => {
                                // An exception in an async callback becomes an unhandled rejection
                                // rather than reaching a subscriber's error handler, which is how a
                                // 404 on /ftue/ stranded the disabled form. This is the floor
                                // under that, for anything _routeAfterLogin cannot resolve itself.
                                this._routeAfterLogin(company, permissionsService)
                                    .catch(error => this.failLogin(error));
                            });
                        });
                    }
                    // The only failure in this chain with nowhere sensible to go: without a
                    // company there are no permissions either, because loadPermissions() reads
                    // this very stream, so no landing route can be resolved.
                }, error => this.failLogin(error));
            }
        });
    }

    /**
     * Chooses where a completed sign-in lands. Everything from here down is route selection, not
     * authentication: processLoginData has already persisted the token and fired afterLogin$ by
     * the time this runs, so no failure in here may leave the user on the login page. That is why
     * /profile/ and /ftue/ are both swallowed to null rather than propagated.
     */
    private async _routeAfterLogin(company: any, permissionsService: PermissionsService): Promise<void> {
        if (this._alreadyDecided()) {
            return;
        }
        if (this.redirectUrl) {
            this._routed = true;
            // The auth guard passes the hash route without its '#' ('/settings/...'); older links
            // carry '#/settings/...'. Assigned bare, the former leaves the SPA for a server path.
            const target = decodeURIComponent(this.redirectUrl);
            window.location.href = target.startsWith('/') ? `#${target}` : target;
            return;
        }
        const defaultRoute = () => this.defaultRouteService.getLoginRoute(
            this.storage, permissionsService, this.storage.selectedLevel?.type);
        try {
            // GET /ftue/ is keyed on the authenticated tenant, not on the selected company, and
            // answers 404 for an invited member, whose JWT carries model 'user' — as does the
            // progress page it leads to, which is why navigation.component sends members to home
            // instead. 6.6 replaced this model gate with a role-only check, and a member's 404
            // then rejected inside the subscribe callback and stranded the disabled form.
            //
            // getRoleName() returns null when current_user_groups is missing or empty (an empty
            // array is truthy, so [0] is undefined). An unresolved role counts as seeing first
            // steps, which is what 6.5.12 did for every tenant before the role check existed.
            const role = company ? (company.getRoleName() || '').toLowerCase() : '';
            const seesFirstSteps = this.storage.model === 'tenant'
                && (!role || progressRoles.includes(role));
            if (!seesFirstSteps) {
                this._leaveTo(defaultRoute());
                return;
            }

            const steps = await this._getFirstSteps();
            // menuClicked is the one thing that can change across the await: the nav menu writes
            // it, and a click there means the user has already chosen where to go.
            if (this._alreadyDecided()) {
                return;
            }
            if (!steps) {
                // A failed or empty /ftue/ decides nothing, so route as if first steps were done.
                // Stated here rather than guarded at each read below, where `!steps?.section1?.q1`
                // would read true and send the tenant to the company setup page instead.
                this._leaveTo(defaultRoute(), 2000);
                return;
            }
            // A falsy company means an empty tree: the guard in _getCompanies only lets one
            // through when the tenant has no companies at all, which is what section1.q1 catches.
            if (!steps.section1?.q1) {
                this._leaveTo(STATE_NAMES.settingsCompanies);
                return;
            }
            const onFirstSteps = steps.progress < 100 && !steps.isClosed;
            this._leaveTo(onFirstSteps ? STATE_NAMES.progress : defaultRoute(), 2000);
        } catch (error) {
            // Permissions are loaded by the time this body runs, so the sign-in can still finish
            // on the user's real landing route rather than bouncing back to the form. If
            // defaultRoute() is itself what threw, the .catch() on the caller takes over.
            this.logger.error(error);
            this._leaveTo(defaultRoute(), 0, true);
        }
    }

    /** True once somewhere else has settled where the user is going. */
    private _alreadyDecided(): boolean {
        return this._routed || !!this.storage.menuClicked;
    }

    /**
     * The single door out of the login page, so that raising `_routed` cannot be forgotten by a
     * branch added later. `delay` exists only for the first-steps paths, which have always waited
     * before navigating.
     */
    private _leaveTo(route: string, delay = 0, reload = false) {
        this._routed = true;
        clearTimeout(this.timer);
        if (!delay) {
            this.routingService.go(route, {}, reload);
            return;
        }
        this.timer = setTimeout(() => {
            if (!this.storage.menuClicked) this.routingService.go(route, {}, reload);
        }, delay);
    }

    /**
     * catchError covers the error channel; defaultIfEmpty covers a source that completes without
     * emitting, which would otherwise reject firstValueFrom with EmptyError.
     */
    private async _getFirstSteps(): Promise<any> {
        return await firstValueFrom(this.firstStepService.getSteps().pipe(
            catchError((error) => {
                this.logger.error(error);
                return of(null);
            }),
            defaultIfEmpty(null)
        ));
    }

    /**
     * A post-authentication step failed with no landing route to fall back on — see the company
     * fetch's error handler in _getCompanies. Re-arms the form so the user can retry, which posts
     * /auth/token/ again and restarts the whole chain.
     *
     * waitOtpToken is cleared because afterLogin() has already nulled mfaToken, leaving the OTP
     * card unusable. Not SignIn.Form.ErrorDefault, which blames the email and password the server
     * just accepted. Deliberately does not set `_routed`: the retry has to be able to navigate.
     *
     * The access token stays in storage, so the user is signed in while looking at the sign-in
     * form. Harmless on the retry path, and the "already authenticated" redirect would not rescue
     * them anyway — its criteria is `login.*`, which matches children of `login`, not `login`.
     */
    private failLogin(error: any) {
        this.logger.error(error);
        if (this._routed) {
            return;
        }
        clearTimeout(this.timer);
        // enable() re-emits valueChanges on every child control and passwordCtrl.valueChanges
        // calls clearErrors(), so the message has to be pushed after the form is re-armed, never
        // before. validationFailed stays false: the error summary renders off errors.length
        // alone, and nothing is wrong with the field itself.
        this.isSubmitting = false;
        this.isTokenSubmitting = false;
        this.submitInFlight = false;
        this.waitOtpToken = false;
        this.clearErrors();
        this.errors.push(this.translate.get('Global.Errors.SomethingWentWrong'));
    }

    signIn() {
        // Enter arrives twice, via the form's (ngSubmit) and via the document:keydown.enter
        // HostListener. The second pass would see the now-disabled form as not valid, wipe the
        // password and report bad credentials over a request still in flight.
        if (this.isSubmitting || this.submitInFlight) {
            return;
        }
        this.submitPressed = true;
        this.submitPressesCount++;

        if (this.form.valid) {
            this.submitInFlight = true;
            //Display captcha only if user failed at least 5 times
            if (this.settingsService.hideRecaptcha) {
                this.resolved();
            } else {
                this.logger.log(`Captcha reseting`);
                this.recaptchaV3Service
                    .execute('importantAction')
                    .pipe(takeUntil(this.destroy$))
                    .subscribe((token: string) => {
                        this.logger.log(`Captcha executed`);
                        this.resolved(token);
                    }, error => {
                        // Without this the flag would stay raised and the form would never accept
                        // another attempt.
                        this.submitInFlight = false;
                        this.logger.error(error);
                    });
            }
        } else {
            this.evaluateErrors();
        }
    }

    @HostListener('document:keydown.enter', ['$event'])
    onKeyEnter(event: KeyboardEvent) {
        if (this.waitOtpToken) {
            this.submitToken();
        } else {
            this.signIn();
        }
    }

    clearErrors() {
        this.errors.length = 0;
    }

    evaluateErrors(serverError?: any) {
        if (this.submitPressed) {
            this.clearErrors();
            const defaultError = this.translate.get(`${this.transPrefix}${this.formPrefix}ErrorDefault`);
            if ('inactive_subscription' in (serverError?.error || {})) {
                let noSubscription;
                if (serverError.error.inactive_subscription === 'The subscription has been cancelled.') {
                    noSubscription = this.translate.get(`${this.transPrefix}${this.formPrefix}NoSubscriptionTenant`)
                } else {
                    noSubscription = this.translate.get(`${this.transPrefix}${this.formPrefix}NoSubscriptionTeam`);
                }
                this.passwordCtrl.reset();
                this.validationFailed = true;
                this.errors.push(noSubscription);
                return;
            }
            if (serverError || !this.form.valid) {
                this.passwordCtrl.reset();
                this.validationFailed = true;
                this.errors.push(defaultError);
            }
        }
    }

    submitToken() {
        // Enter reaches here twice — once via the document keydown listener and once via the
        // form's implicit submit. Without this guard the second call runs against the disabled
        // form, whose value omits disabled controls, and posts an undefined token that the
        // server then rejects. It also covers double-clicking the button.
        if (this.isTokenSubmitting) {
            return;
        }
        this.tokenSubmitPressed = true;
        if (this.tokenCtrl.invalid) {
            return;
        }
        let value = this.tokenForm.value;
        this.isTokenSubmitting = true;
        this.storage.menuClicked = null;
        this.authService.auth2faToken(this.storage.mfaToken, value.otpToken)
            .pipe(takeUntil(this.destroy$))
            .subscribe(data => {
                this.afterLogin();
            }, error => {
                this.isTokenSubmitting = false;
                if (this.submitPressed) {
                    this.clearErrors();
                    const defaultError = this.translate.get(`2fa.ErrorDefault`);
                    this.passwordCtrl.reset();
                    this.validationTokenFailed = true;
                    this.errors.push(defaultError);
                }
                this.logger.error(error);
            });
    }

    cancel() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
        this.storage.mfaToken = null;
        this.routingService.go(STATE_NAMES.login, {}, true);
    }

    getSubscription() {
        // Non-fatal on purpose: only caches two flags, and runs in parallel with the company load.
        this.authService.getSubscription().pipe(takeUntil(this.destroy$)).subscribe(subscription => {
            const tenantCancelledSub = subscription.status === 'cancelled';
            this.storage.setStorageItem('tenantCancelledSub', tenantCancelledSub);
            const tenantTrialSub = subscription.status === 'in_trial';
            this.storage.setStorageItem('tenantTrialSub', tenantTrialSub);
        }, error => this.logger.error(error));
    }

    ngOnDestroy() {
        this.renderer.removeClass(document.body, 'show-recaptcha');

        clearTimeout(this.timer);

        this.destroy$.next();
        this.destroy$.complete();
    }
}
